// Public privacy policy — registered as the App Store Connect privacy policy
// URL (#336). Keep this page in sync with what the apps actually collect;
// the working inventory lives in docs/APP_STORE.md.
import LegalPage from './LegalPage'

export default function PrivacyPolicy() {
  return (
    <LegalPage eyebrow="Legal" title="Privacy Policy" updated="23 August 2026">
      <p>
        GroundOps Tech Pty Ltd (ACN 699 269 380) (&ldquo;GroundOps&rdquo;, &ldquo;we&rdquo;,
        &ldquo;us&rdquo;) provides field reporting software for civil construction teams: a mobile
        app for field crews (GroundOps Field) and a web platform for office staff. This policy
        explains what personal information we collect, why we collect it, and the choices you have.
        It applies to the GroundOps mobile and web applications and this website.
      </p>
      <p>
        GroundOps is a workplace tool. Any business can create its own GroundOps workspace at
        groundops.app or from the mobile app; member accounts within a workspace are created by
        invitation from that organisation&rsquo;s administrators. Your organisation controls the
        workspace your data belongs to.
      </p>

      <h2>Information we collect</h2>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>What</th>
            <th>Why</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Account details</td>
            <td>Name, email address, phone number, role, organisation, optional profile photo</td>
            <td>Sign-in (SMS one-time codes), identifying you to your team, account administration</td>
          </tr>
          <tr>
            <td>Job records</td>
            <td>Job addresses, asset identifiers, job status, crew assignments, timestamps</td>
            <td>The core function of the product — documenting field work</td>
          </tr>
          <tr>
            <td>Precise location</td>
            <td>Device GPS coordinates when you create a job or record where work was carried out</td>
            <td>Recording accurately where installations happened; requested only when you use those features</td>
          </tr>
          <tr>
            <td>Photos</td>
            <td>Job evidence photos you capture against checklists, and profile photos you choose</td>
            <td>Work verification and reporting; profile personalisation</td>
          </tr>
          <tr>
            <td>Messages</td>
            <td>Chat messages you send on jobs</td>
            <td>Team coordination on that job</td>
          </tr>
          <tr>
            <td>Compliance documents &amp; signatures</td>
            <td>
              Safety and compliance forms you fill in, drawn or typed signatures and acknowledgements you
              record against documents, when you signed, and whether the document was read through before
              signing
            </td>
            <td>
              Compliance records your organisation is required to keep (for example SWMS sign-ons and daily
              pre-start checks) and evidencing that signatures were properly captured
            </td>
          </tr>
          <tr>
            <td>Device &amp; session data</td>
            <td>Sign-in session tokens, IP address, standard server access logs</td>
            <td>Keeping you signed in, security, and diagnosing faults</td>
          </tr>
        </tbody>
      </table>
      <p>
        We do not collect data for advertising, we do not track you across other companies' apps or
        websites, and we do not sell personal information. The mobile app contains no advertising or
        analytics SDKs.
      </p>

      <h2>How your information is used</h2>
      <ul>
        <li>To operate the product: signing you in, showing jobs to your team, storing photos and messages.</li>
        <li>To notify you: job and team notifications in-app, by email, and &mdash; if you allow notifications on your device &mdash; as push notifications on your phone, according to your notification preferences.</li>
        <li>To keep the service secure: authorisation checks, abuse prevention, and error monitoring.</li>
        <li>To support you: responding when you contact us.</li>
      </ul>

      <h2>Who can see your information</h2>
      <p>
        Your work data (jobs, photos, messages, name, role) is visible to members of your
        organisation according to their role — for example, office staff can review submitted jobs.
        We do not share personal information outside your organisation except with the service
        providers below, or where the law requires it.
      </p>

      <h2>Service providers</h2>
      <p>We use a small number of processors to run GroundOps:</p>
      <ul>
        <li><strong>Amazon Web Services</strong> — application hosting, database, and photo storage (Sydney, Australia region).</li>
        <li><strong>Prelude</strong> — delivers the SMS verification codes used to sign in to the mobile app.</li>
        <li><strong>Resend</strong> — delivers transactional email (invitations, notifications).</li>
        <li><strong>Google Maps</strong> — renders maps in the apps; map tiles are requested from Google when you view a map.</li>
        <li><strong>Sentry</strong> — error and crash monitoring so we can fix faults. This runs in our servers and, from recent versions, in the mobile app: if the app hits an error or crashes, a report is sent describing what went wrong (error details, app version, device model). Crash reports are linked to your account ID only — never your name, email, phone number, or photos.</li>
        <li><strong>Expo</strong> &mdash; delivers push notifications to your device. If you turn push notifications on, your device registers a push token with Expo, and the notification text (for example, a job reference and which photo categories need re-work) passes through Expo and then Apple or Google to reach your phone.</li>
        <li><strong>Apple</strong> — App Store distribution of the iOS app.</li>
        <li><strong>Google</strong> — Android app distribution via Google Play.</li>
      </ul>

      <h2>Where your data is stored</h2>
      <p>
        GroundOps data is stored in Australia (AWS Sydney region). Some providers above may process
        limited data (such as a phone number receiving an SMS) in other regions as part of
        delivering their service.
      </p>

      <h2>Retention and deletion</h2>
      <p>
        We keep your information while your account is active. Job records, photos, and messages
        form part of your organisation's compliance and work history, so they may be retained by
        your organisation after you leave it.
      </p>
      <div className="legal-callout">
        <p>
          <strong>Deleting your account:</strong> you can request deletion at{' '}
          <a href="https://app.groundops.app/delete-account">app.groundops.app/delete-account</a>{' '}
          (no app install needed), via <em>Profile → Request account deletion</em> in the GroundOps
          Field app, or by emailing{' '}
          <a href="mailto:support@groundops.app">support@groundops.app</a>. If your account belongs
          to an organisation someone else runs, an administrator confirms the request, then your
          personal details are removed; records your organisation must keep for legal or
          contractual reasons may be retained with your personal identifiers removed where
          practicable.
        </p>
        <p>
          <strong>Deleting a workspace:</strong> if you are the only owner of your organisation,
          deleting your account deletes the whole workspace. Access ends immediately for everyone
          in it, and nothing is erased for 30 days — the link in your confirmation email restores
          everything during that time. After 30 days the workspace is permanently deleted from our
          live systems: photographs and files are deleted outright, including every stored
          version — we keep no backup copy of them. Records in our encrypted database backups are
          removed as those backups expire, within 30 days of the deletion.
        </p>
      </div>

      <h2>Your choices</h2>
      <ul>
        <li>Camera, location, and photo-library access are requested only when a feature needs them, and the app remains usable if you decline (for example, job addresses can be entered manually).</li>
        <li>You can view and update your name, email, phone number, and profile photo in the app.</li>
        <li>You can ask us to access, correct, or delete your personal information at any time via <a href="mailto:support@groundops.app">support@groundops.app</a>.</li>
      </ul>

      <h2>Children</h2>
      <p>
        GroundOps is a workplace tool for invited organisation members and is not directed at
        children.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we make material changes we will update this page and the date above, and notify
        organisation administrators.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or requests:{' '}
        <a href="mailto:support@groundops.app">support@groundops.app</a>.
      </p>
    </LegalPage>
  )
}
